import { Calendar, Tag, Clock, ArrowLeft, Mail, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import SocialShare from '../../../components/SocialShare';
import SEO from '../../../components/SEO';
import { useState } from 'react';
import BottomCTA from '@/components/BottomCTA';
import { client } from '../../../tina/__generated__/client';
import { useTina } from 'tinacms/dist/react';
import { TinaMarkdown } from 'tinacms/dist/rich-text';
import Image from 'next/image';

// Helper function to render rich text content
const renderRichText = (content) => {
  if (!content?.children) return null;

  return content.children.map((node, index) => {
    // Handle paragraphs
    if (node.type === "p") {
      return (
        <p key={index} className="text-slate-600 leading-relaxed mb-4">
          {node.children.map((child, childIndex) => {
            if (child.type === "text") {
              return <span key={childIndex}>{child.text}</span>;
            }
            if (child.type === "a") {
              return (
                <a
                  key={childIndex}
                  href={child.url}
                  className="text-sky-500 hover:text-sky-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {child.children[0].text}
                </a>
              );
            }
            return null;
          })}
        </p>
      );
    }

    // Handle headings
    if (node.type === "h1") {
      return (
        <h1 key={index} className="text-4xl font-bold text-slate-900 mt-8 mb-4">
          {node.children[0].text}
        </h1>
      );
    }
    if (node.type === "h2") {
      return (
        <h2 key={index} className="text-3xl font-bold text-slate-900 mt-8 mb-4">
          {node.children[0].text}
        </h2>
      );
    }
    if (node.type === "h3") {
      return (
        <h3 key={index} className="text-2xl font-bold text-slate-900 mt-6 mb-3">
          {node.children[0].text}
        </h3>
      );
    }

    // Handle lists
    if (node.type === "ul") {
      return (
        <ul key={index} className="list-disc pl-6 space-y-2 text-slate-600 mb-4">
          {node.children.map((item, itemIndex) => (
            <li key={itemIndex}>{item.children[0].text}</li>
          ))}
        </ul>
      );
    }
    if (node.type === "ol") {
      return (
        <ol key={index} className="list-decimal pl-6 space-y-2 text-slate-600 mb-4">
          {node.children.map((item, itemIndex) => (
            <li key={itemIndex}>{item.children[0].text}</li>
          ))}
        </ol>
      );
    }

    // Handle blockquotes
    if (node.type === "blockquote") {
      return (
        <blockquote key={index} className="border-l-4 border-sky-500 pl-4 italic text-slate-600 my-4">
          {node.children[0].text}
        </blockquote>
      );
    }

    // Handle code blocks
    if (node.type === "code_block") {
      return (
        <pre key={index} className="bg-slate-100 p-4 rounded-lg overflow-x-auto my-4">
          <code className="text-sm text-slate-800">{node.children[0].text}</code>
        </pre>
      );
    }

    return null;
  });
};

// Helper function to convert category name to slug
const getCategorySlug = (categoryName) => {
    const slugMap = {
        'eIDAS 2.0 Explained': 'eidas-explained',
        'Getting Started': 'getting-started',
        'Business Applications': 'business-applications',
        'Security & Privacy': 'security-and-privacy',
        'Use Cases': 'use-cases'
    };
    return slugMap[categoryName] || categoryName.toLowerCase().replace(/\s+/g, '-');
};

export default function BlogPost({ data, variables, query }) {
  const { data: tinaData } = useTina({
    data,
    variables,
    query,
  });
  const post = tinaData.blogConnection.edges[0].node;
  const [email, setEmail] = useState('');

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    // Placeholder for future functionality
    console.log('Email submitted:', email);
  };

  const ogImageUrl = `https://businesswallet.eu/api/blog/image/${post.slug}`;
  const mainImageUrl = post?.mainImage ? post?.mainImage : ogImageUrl;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${post.title} | BusinessWallet.eu Blog`}
        description={post.excerpt || post.content?.children?.[0]?.children?.[0]?.text || 'Read more about this article on our blog'}
        ogImage={mainImageUrl}
        ogUrl={`https://businesswallet.eu/blog/${getCategorySlug(post.category?.name)}/${post.slug}`}
      />
      <Header />
      <main>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Link
            href="/blog"
            className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8 mt-12"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blogs
          </Link>

          <article className="prose prose-slate max-w-none">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center gap-4 text-sm text-slate-500 mb-4">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                <div className="flex items-center">
                  <Tag className="w-4 h-4 mr-1" />
                  {post?.category?.name}
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {post.readTime}
                </div>
              </div>

              <h1 className="text-4xl font-bold text-slate-900 mb-6">
                {post.title}
              </h1>

              {post.author && (
                <div className="flex items-center space-x-4 mb-8">
                  {post.author.avatar && (
                    <img
                      src={post.author.avatar}
                      alt={post.author.name}
                      className="w-12 h-12 rounded-full"
                    />
                  )}
                  <div>
                    <div className="font-medium text-slate-900">
                      {post.author.name}
                    </div>
                    {post.author.role && (
                      <div className="text-sm text-slate-500">
                        {post.author.role}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Image
                width={300}
                height={300}
                src={mainImageUrl}
                alt={post.title}
                className="w-full aspect-video rounded-2xl shadow-sm mb-8 object-cover"
              />

              <div className="border-t border-b border-slate-200 py-4 mb-8">
                <SocialShare
                  title={post.title}
                  url={`https://businesswallet.eu/blog/${getCategorySlug(post.category?.name)}/${post.slug}`}
                />
              </div>
            </div>

            <div className="space-y-6">
              <TinaMarkdown
                content={post.content}
                components={{
                  p: ({ children }) => (
                    <p className="text-slate-600 leading-relaxed mb-4">{children}</p>
                  ),
                  h1: ({ children }) => (
                    <h1 className="text-4xl font-bold text-slate-900 mt-8 mb-4">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-3xl font-bold text-slate-900 mt-8 mb-4">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-2xl font-bold text-slate-900 mt-6 mb-3">{children}</h3>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-6 space-y-2 text-slate-600 mb-4">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-6 space-y-2 text-slate-600 mb-4">{children}</ol>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-sky-500 pl-4 italic text-slate-600 my-4">{children}</blockquote>
                  ),
                  code_block: ({ children }) => (
                    <pre className="bg-slate-100 p-4 rounded-lg overflow-x-auto my-4">
                      <code className="text-sm text-slate-800">{children}</code>
                    </pre>
                  ),
                  a: ({ url, children }) => (
                    <a
                      href={url}
                      className="text-sky-500 hover:text-sky-600 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              />
            </div>
          </article>
        </div>

        {/* CTA Section */}
        <BottomCTA />
      </main>
      <Footer />
    </div>
  );
}

export async function getStaticPaths() {
  const { data } = await client.request({
    query: `#graphql
      query {
        blogConnection {
          edges {
            node {
              slug
              category {
                name
              }
            }
          }
        }
      }
    `
  });

  const paths = data.blogConnection.edges
    .filter(edge => edge.node.category?.name) // Only include posts with categories
    .map(edge => ({
      params: { 
        category: getCategorySlug(edge.node.category.name),
        slug: edge.node.slug 
      }
    }));

  return {
    paths,
    fallback: "blocking"
  };
}

export async function getStaticProps({ params }) {
  const response = await client.queries.blogConnection({
    filter: {
      slug: { eq: params.slug }
    }
  });

  // Verify that the post belongs to the specified category
  const post = response.data.blogConnection.edges[0]?.node;
  if (!post || !post.category || 
      getCategorySlug(post.category.name) !== params.category) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data: response.data,
      query: response.query,
      variables: response.variables,
    },
    revalidate: 60, // Revalidate every minute
  };
} 